import {
    BoxedRow,
    ButtonPrimary,
    Callout,
    Divider,
    IconChevronLeftRegular,
    IconInformationUserRegular,
    NavigationBar,
    RadioGroup,
    skinVars,
    Text2,
    Text3,
    useDialog,
} from '@telefonica/mistica';
import { t } from 'i18next';
import { getRoleLine } from '../../../utils/dataTransformation/productConsumption';
import { useConfigureLineContext } from '../../../context/configure-line-context/ConfigureLineContext';
import { SimSelectionType } from '../types';
import './index.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import { duplicateESIM, getSIMSInfo } from '../../../services/ConfigureLineService';
import { useQuery } from '@tanstack/react-query';
import BreadCrumbsWrapper from '../../../components/BreadcrumbsWrapper';
import { managementEnvironment } from '../../../utils/dataTransformation/environments';
import { AxiosError } from 'axios';
import { usePersonalDataContext } from '../../../context/personal-data-context/PersonalDataContext';

const DuplicateSim = () => {
    const {
        simTypeSelected,
        setSimTypeSelected,
        lineActive,
        isConvergent,
        setConfigureLineError,
        setDuplicateSimSuccess,
    } = useConfigureLineContext();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { personalData } = usePersonalDataContext();
    const { dialog } = useDialog();
    const duplicateEsimEnabled = managementEnvironment().VITE_WEBVIEW_DUPLICATE_ESIM_ENABLED;
    const consumptions = JSON.stringify(window.localStorage.getItem('storages'));
    const { data: simInfo } = useQuery({
        queryKey: ['sims-info', lineActive],
        queryFn: () => getSIMSInfo(lineActive),
        enabled: !!lineActive,
    });
    const isMultiSim = pathname.includes('multiSim');

    const navigationBackHandler = () => navigate(`/configure-line`);

    const simRequest = async () => {
        try {
            if (duplicateEsimEnabled) await duplicateESIM(simInfo?.productId!);

            localStorage.setItem('timestampDuplicate', JSON.stringify({ time: `${new Date()}` }));
            setDuplicateSimSuccess(true);
        } catch (error) {
            setDuplicateSimSuccess(false);
            setConfigureLineError(error as AxiosError);
        } finally {
            navigate(`/configure-line/sim/feedbackDuplicateSIM`);
        }
    };

    const saveHandler = () => {
        if (simTypeSelected === 'PHYSICAL_SIM') {
            if (isMultiSim) navigate(`/configure-line/multiSim/duplicate/request`);
            else {
                navigate('/configure-line/sim/duplicate/request');
            }
        } else {
            if (personalData?.fiscalIdType === 'CIF_') {
                dialog({
                    className: 'action-noAvailable-cif',
                    asset: <IconInformationUserRegular color={skinVars.colors.backgroundBrandSecondary} size={40} />,
                    title: t('actionNoAvailable'),
                    acceptText: t('buttonUnderstoodFeedback'),
                    message: t('NoAvailableCIFDaon'),
                    onAccept() {},
                });
            } else if (isMultiSim) navigate(`/configure-line/multiSim/duplicate/request`);
            else simRequest();
        }
    };

    const simSelectionHandler = (value: SimSelectionType) => setSimTypeSelected(value);

    return (
        <div className='duplicate-sim'>
            <div className='duplicate-sim__header'>
                {!(isWebviewAndroid || isWebviewIOS) && (
                    <div>
                        <div className='duplicate-sim__header-navigationBar'>
                            <NavigationBar
                                withBorder={false}
                                onBack={navigationBackHandler}
                                title={t(isMultiSim ? 'multiSimRequestHeader' : 'duplicateRequest')}
                            />
                        </div>
                        <div className='duplicate-sim__header-return' onClick={navigationBackHandler}>
                            <div className='duplicate-sim__header-return-icon'>
                                <IconChevronLeftRegular color={skinVars.colors.textPrimary} size={20} />
                            </div>
                            <Text2 medium color={skinVars.colors.textPrimary}>
                                {t('returnResponsive')}
                            </Text2>
                        </div>
                    </div>
                )}

                <div className='duplicate-sim__breadcrumbs'>
                    <BreadCrumbsWrapper
                        title={isMultiSim ? t('multiSimRequestHeader') : t('duplicateRequest')}
                        breadcrumbs={
                            !isConvergent
                                ? [
                                      { title: t('breadcrumbsHome'), url: '/home' },
                                      {
                                          title: t('lineManagement'),
                                          url: `/configure-line`,
                                      },
                                  ]
                                : [
                                      { title: t('breadcrumbsHome'), url: '/home' },
                                      {
                                          title: getRoleLine(JSON.parse(consumptions), simInfo?.productPsId!),
                                          url: `/mobile?productPsId=${simInfo?.productPsId}`,
                                      },
                                      {
                                          title: t('lineManagement'),
                                          url: `/configure-line`,
                                      },
                                  ]
                        }
                    />
                </div>
            </div>

            <Divider />

            <div className='duplicate-sim__body'>
                <Text3 weight='regular'>{t(isMultiSim ? 'selectMultiSimType' : 'selectSimType')}</Text3>

                <RadioGroup
                    name='sim-selection-radio'
                    value={simTypeSelected}
                    onChange={(value) => simSelectionHandler(value as SimSelectionType)}
                >
                    <BoxedRow title='SIM' radioValue='PHYSICAL_SIM' />
                    {duplicateEsimEnabled && (
                        <BoxedRow title='eSIM' description={t('duplicateESIMVersionDigital')} radioValue='E_SIM' />
                    )}
                </RadioGroup>

                {isMultiSim && (
                    <Callout
                        asset={<IconInformationUserRegular color={skinVars.colors.brand} size={24} />}
                        description={t('multiSimCostCallout')}
                    />
                )}
            </div>

            <div className='duplicate-sim__bottom'>
                <ButtonPrimary role='onPress' onPress={saveHandler}>
                    {t('continue')}
                </ButtonPrimary>
            </div>
        </div>
    );
};

export default DuplicateSim;
